/**
 * F 474 — THE CREDENTIAL'S OTHER HOME IS THE GRAPH.
 *
 * WHAT THIS IS
 * ------------
 * The credential store's storage seam (`CredentialStorage`) is the ONE place that
 * says where a sealed credential record lives. Until F 474 it was always the
 * browser's IndexedDB (`freshcards:credentials` / `records`), and that is why
 * the operator's Groq key — saved on the laptop — did not exist on the phone.
 * The key was not lost. It was in a place the phone does not have.
 *
 * MEASURED on production before any of this was written:
 *
 *     $ curl -H "Authorization: Bearer $TOKEN" ".../kv?prefix=freshcards:credential:"
 *     credential nodes in the graph: 1
 *       freshcards:credential:user:dev-freshcards-local:deepseek-web:pipeline
 *
 * The DeepSeek cookie is there because its card calls `saveToGraph` (F 390).
 * The Groq key is not, because the provider slot calls `connect` (F 439) and
 * `connect` never wrote the node. The graph held a POINTER to a secret it
 * could not see.
 *
 * SO THIS MODULE MAKES THE GRAPH A READ SOURCE
 * ---------------------------------------------
 * A read that misses locally falls through to the graph, and the graph's node
 * is converted back into the store's own `StoredCredential` — the exact inverse
 * of `credentialNodeFrom`. Every consumer of the store (the chat, the settings
 * page, the provider slot's badge) then sees the key with no other change,
 * because the STORE is what they all read through.
 *
 * WHY THE GRAPH READ IS FREE
 * ---------------------------
 * `RemoteState.init()` already hydrates the whole graph into an in-memory cache
 * with `GET /kv?all=1`, and `remoteAdapter.load(key)` reads that cache. The
 * credential nodes live under `freshcards:` so they are syncable, so they are in
 * the cache, so this read costs NO network call. It is a cache hit.
 *
 * WHY IT WAITS FOR THE HYDRATION — AND WHY ONLY SOMETIMES
 * --------------------------------------------------------
 * The cache is empty until `init()` finishes, and the chat resolves the key
 * when the user sends a message. So the first graph read waits for the
 * hydration to complete. But `init()` is not called in a unit test, and
 * `whenHydrated()` would then never resolve — a promise that never settles is a
 * hang, and a hang in a test suite is worse than a missing key. So the wait is
 * CONDITIONAL: it happens only when the build actually has a graph token, which
 * is exactly the build that also calls `init()`.
 *
 * WHAT THIS DOES NOT DO
 * ---------------------
 * It does not change the SEAL. The node is sealed by the store with the user's
 * passphrase and carries the record's salt, so a holder of the passphrase opens
 * it on any device — the passphrase resolver derives from the RECORD's own
 * salt, and that salt travels in the node. F 425 pins the salt strategy (the
 * passphrase branch mints a random per-record salt; the session-key branch uses
 * the label salt) and this module does not touch it.
 *
 * The consequence, measured rather than glossed: a device that has just SIGNED
 * IN holds the passphrase in memory and can open the graph's node. A device
 * that has RELOADED holds only the session key — PBKDF2(passphrase, labelSalt)
 * — and cannot open a record sealed under a random salt. That is a property of
 * the F 425 seal design, not of this read path, and the report says so.
 */

import { RemoteState } from '../storage/RemoteState';
import { graphAuthProblem } from '../storage/graphAuth';
import { credentialGraphKey, CREDENTIAL_GRAPH_PREFIX } from './credentialSettings';
import type { CredentialStorage, Principal, StoredCredential } from './credentialStore';

/**
 * The graph's `credential` node, as stored.
 *
 * This is the `Credential` model, narrowed to the fields the READ path needs.
 * `encryptedValue` is the ciphertext — the node never carries the plaintext.
 */
export interface GraphCredentialNode {
  id: string;
  name: string;
  label: string;
  provider: string;
  kind: string;
  scope: string;
  principal?: { scope?: string; id?: string };
  encryptedValue: string;
  iv: string;
  salt: string;
  algorithm: string;
  kdf: string;
  createdAt?: string;
  updatedAt?: string;
  description?: string;
}

/**
 * The inverse of `credentialNodeFrom` — a graph node back into the store's
 * own record.
 *
 * It is the exact inverse on purpose: the write path and the read path must
 * agree about what a record IS, or a key that was saved cannot be opened. The
 * field names differ (`encryptedValue` on the wire, `ciphertext` in the store)
 * and that is the whole point of a conversion existing.
 *
 * Returns null for a node that is not a credential node — a graph can hold
 * anything, and a malformed node must not crash a read.
 */
export function credentialRecordFromNode(node: unknown): StoredCredential | null {
  if (!node || typeof node !== 'object') return null;
  const n = node as Partial<GraphCredentialNode>;
  if (typeof n.id !== 'string' || !n.id) return null;
  if (typeof n.encryptedValue !== 'string' || !n.encryptedValue) return null;
  if (typeof n.iv !== 'string' || !n.iv) return null;
  if (typeof n.salt !== 'string' || !n.salt) return null;
  const createdAt = n.createdAt ? Date.parse(n.createdAt) : 0;
  const updatedAt = n.updatedAt ? Date.parse(n.updatedAt) : 0;
  return {
    id: n.id,
    name: n.name ?? 'pipeline',
    label: n.label ?? n.provider ?? n.id,
    provider: n.provider ?? 'unknown',
    kind: (n.kind ?? 'api_key') as StoredCredential['kind'],
    scope: (n.scope ?? 'operator') as StoredCredential['scope'],
    present: true,
    createdAt: Number.isFinite(createdAt) ? createdAt : 0,
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : 0,
    description: n.description,
    ciphertext: n.encryptedValue,
    iv: n.iv,
    salt: n.salt,
    algorithm: n.algorithm ?? 'AES-GCM',
    kdf: (n.kdf ?? 'pbkdf2') as StoredCredential['kdf'],
  };
}

/**
 * Does this build have a graph at all?
 *
 * `graphAuthProblem()` names the reason when it does not (no build-time token),
 * which is the same reason the app uses everywhere else. A build with no graph
 * must not WAIT for a hydration that will never bring one.
 */
export function graphAvailable(): boolean {
  return graphAuthProblem() === null;
}

/**
 * Wait for the graph to be hydrated — but only when there IS a graph.
 *
 * Bounded by the caller: the wait is a promise that resolves when
 * `RemoteState.init()` finishes, and `init()` finishes on every path (F 470
 * made the completion a fact, not a hope).
 */
async function whenGraphReady(): Promise<void> {
  if (!graphAvailable()) return;
  await RemoteState.whenHydrated();
}

/** Read one credential node from the graph, or null. */
export async function readGraphCredential(id: string): Promise<StoredCredential | null> {
  if (!graphAvailable()) return null;
  try {
    await whenGraphReady();
    const raw = await RemoteState.remoteAdapter.load(credentialGraphKey(id));
    if (!raw) return null;
    return credentialRecordFromNode(raw);
  } catch {
    // A graph that cannot be read is a graph that has no answer, not a crash.
    return null;
  }
}

/** The ids of every credential node in the graph. */
export async function listGraphCredentialIds(): Promise<string[]> {
  if (!graphAvailable()) return [];
  try {
    await whenGraphReady();
    const keys = await RemoteState.remoteAdapter.list(CREDENTIAL_GRAPH_PREFIX);
    return keys
      .map((k) => k.slice(CREDENTIAL_GRAPH_PREFIX.length))
      .filter((id) => id.length > 0);
  } catch {
    return [];
  }
}

/**
 * THE STORAGE: local first, then the graph.
 *
 * `put` and `delete` are LOCAL ONLY, and that is a deliberate boundary:
 * the graph write is an EXPLICIT step in the save action (`writeCredentialNode`),
 * because the graph is the credential's canonical home and writing it is a
 * decision, not a side effect of a cache write. A store that silently
 * published every credential it touched would be a store with two homes and no
 * owner.
 */
export function createGraphCredentialStorage(opts: {
  local: CredentialStorage;
  /** The principal whose credentials this store reads. */
  principal?: Principal;
}): CredentialStorage {
  const { local, principal } = opts;
  /** Records read out of the graph this session, so a second read is free. */
  const fromGraph = new Map<string, StoredCredential>();

  return {
    async get(id) {
      const hit = await local.get(id);
      if (hit) return hit;
      const cached = fromGraph.get(id);
      if (cached) return cached;
      const rec = await readGraphCredential(id);
      if (rec) fromGraph.set(id, rec);
      return rec ?? undefined;
    },
    async put(rec) {
      await local.put(rec);
      // The graph copy is written by the save action, not here.
      fromGraph.delete(rec.id);
    },
    async delete(id) {
      await local.delete(id);
      fromGraph.delete(id);
    },
    async list() {
      const localRecords = await local.list();
      const seen = new Set(localRecords.map((r) => r.id));
      const out = [...localRecords];
      for (const id of await listGraphCredentialIds()) {
        if (seen.has(id)) continue;
        const rec = await readGraphCredential(id);
        if (!rec) continue;
        if (principal && rec.scope !== principal.scope) continue;
        if (principal && !rec.id.startsWith(`${principal.scope}:${principal.id}:`)) continue;
        seen.add(id);
        fromGraph.set(id, rec);
        out.push(rec);
      }
      return out;
    },
  };
}
